import React, { useEffect, useState, createContext, useContext } from 'react';
import { usePresentationMode } from './PresentationModeContext';
type CustomerInsight = {
  type: 'behavior' | 'interest' | 'objection' | 'readiness' | 'suggestion';
  message: string;
  severity: 'info' | 'warning' | 'success' | 'error';
  timestamp: Date;
  id: string;
  dismissed?: boolean;
  category?: string;
};
type InterestPoint = {
  section: string;
  level: number; // 0-100
  timeSpent: number; // seconds
};
type CustomerProfile = {
  priceLevel: 'budget' | 'moderate' | 'luxury' | 'unknown';
  decisionStyle: 'analytical' | 'emotional' | 'collaborative' | 'directive' | 'unknown';
  pacePreference: 'fast' | 'moderate' | 'slow' | 'unknown';
  objections: string[];
  interests: InterestPoint[];
  readinessScore: number; // 0-100
  viewedSections: string[];
};
interface ClosingAssistantContextType {
  isEnabled: boolean;
  isMinimized: boolean;
  activeTab: string;
  insights: CustomerInsight[];
  customerProfile: CustomerProfile;
  recentActivity: string[];
  closingReadiness: 'low' | 'medium' | 'high';
  toggleEnabled: () => void;
  toggleMinimized: () => void;
  setActiveTab: (tab: string) => void;
  dismissInsight: (id: string) => void;
  clearInsights: () => void;
  recordSectionView: (section: string) => void;
  recordInterestLevel: (section: string, level: number) => void;
  recordTimeSpent: (section: string, seconds: number) => void;
  updateReadinessScore: (score: number) => void;
  addRecentActivity: (activity: string) => void;
}
const ClosingAssistantContext = createContext<ClosingAssistantContextType | undefined>(undefined);
export const ClosingAssistantProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    isPresentationMode
  } = usePresentationMode();
  const [isEnabled, setIsEnabled] = useState(true);
  const [isMinimized, setIsMinimized] = useState(true);
  const [activeTab, setActiveTab] = useState('insights');
  const [insights, setInsights] = useState<CustomerInsight[]>([]);
  const [recentActivity, setRecentActivity] = useState<string[]>([]);
  const [closingReadiness, setClosingReadiness] = useState<'low' | 'medium' | 'high'>('low');
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile>({
    priceLevel: 'unknown',
    decisionStyle: 'unknown',
    pacePreference: 'unknown',
    objections: [],
    interests: [],
    readinessScore: 20,
    viewedSections: []
  });
  // Disable in presentation mode
  useEffect(() => {
    if (isPresentationMode) {
      setIsEnabled(false);
    }
  }, [isPresentationMode]);
  // Add a new insight
  const addInsight = (insight: Omit<CustomerInsight, 'id' | 'timestamp'>) => {
    const newInsight = {
      ...insight,
      id: Date.now().toString(),
      timestamp: new Date(),
      dismissed: false
    };
    setInsights(prev => [newInsight, ...prev]);
    // Auto-expand the assistant when important insights come in
    if (insight.severity === 'success' || insight.severity === 'error') {
      setIsMinimized(false);
    }
  };
  // Dismiss a specific insight
  const dismissInsight = (id: string) => {
    setInsights(prev => prev.map(insight => insight.id === id ? {
      ...insight,
      dismissed: true
    } : insight));
  };
  // Clear all insights
  const clearInsights = () => {
    setInsights([]);
  };
  // Record that a section was viewed
  const recordSectionView = (section: string) => {
    if (!customerProfile.viewedSections.includes(section)) {
      setCustomerProfile(prev => ({
        ...prev,
        viewedSections: [...prev.viewedSections, section]
      }));
      // Generate insight based on the section
      if (section === 'price') {
        addInsight({
          type: 'behavior',
          message: 'Customer is checking pricing details - potential price sensitivity',
          severity: 'info',
          category: 'pricing'
        });
      } else if (section === 'itinerary') {
        addInsight({
          type: 'interest',
          message: 'Customer is reviewing itinerary details - showing interest in activities',
          severity: 'info',
          category: 'product'
        });
      } else if (section === 'overview') {
        addInsight({
          type: 'behavior',
          message: 'Customer is viewing overview - gathering general information',
          severity: 'info',
          category: 'product'
        });
      }
    }
  };
  // Record interest level in a section
  const recordInterestLevel = (section: string, level: number) => {
    setCustomerProfile(prev => {
      const existingIndex = prev.interests.findIndex(i => i.section === section);
      let newInterests = [...prev.interests];
      if (existingIndex >= 0) {
        newInterests[existingIndex] = {
          ...newInterests[existingIndex],
          level
        };
      } else {
        newInterests.push({
          section,
          level,
          timeSpent: 0
        });
      }
      return {
        ...prev,
        interests: newInterests
      };
    });
    // Generate insights based on high interest
    if (level > 80) {
      addInsight({
        type: 'interest',
        message: `High interest detected in ${section} - consider highlighting related benefits`,
        severity: 'success',
        category: 'opportunity'
      });
    }
  };
  // Record time spent on a section
  const recordTimeSpent = (section: string, seconds: number) => {
    setCustomerProfile(prev => {
      const existingIndex = prev.interests.findIndex(i => i.section === section);
      let newInterests = [...prev.interests];
      if (existingIndex >= 0) {
        newInterests[existingIndex] = {
          ...newInterests[existingIndex],
          timeSpent: newInterests[existingIndex].timeSpent + seconds
        };
      } else {
        newInterests.push({
          section,
          level: 50,
          timeSpent: seconds
        });
      }
      return {
        ...prev,
        interests: newInterests
      };
    });
    // Generate insights based on significant time spent
    if (seconds > 30) {
      addInsight({
        type: 'behavior',
        message: `Customer spent significant time (${seconds}s) on ${section}`,
        severity: 'info',
        category: 'engagement'
      });
    }
  };
  // Update readiness score and generate appropriate insights
  const updateReadinessScore = (score: number) => {
    const prevScore = customerProfile.readinessScore;
    setCustomerProfile(prev => ({
      ...prev,
      readinessScore: score
    }));
    // Update closing readiness level
    if (score < 40) {
      setClosingReadiness('low');
    } else if (score < 70) {
      setClosingReadiness('medium');
    } else {
      setClosingReadiness('high');
    }
    // Generate insights based on score changes
    if (prevScore < 70 && score >= 70) {
      addInsight({
        type: 'readiness',
        message: 'Customer appears ready to book - consider closing now',
        severity: 'success',
        category: 'closing'
      });
    } else if (prevScore < 40 && score >= 40) {
      addInsight({
        type: 'readiness',
        message: 'Customer engagement increasing - start introducing booking options',
        severity: 'info',
        category: 'closing'
      });
    }
  };
  // Add a recent activity
  const addRecentActivity = (activity: string) => {
    setRecentActivity(prev => [activity, ...prev.slice(0, 9)]);
  };
  // Toggle the enabled state
  const toggleEnabled = () => {
    setIsEnabled(prev => !prev);
  };
  // Toggle the minimized state
  const toggleMinimized = () => {
    setIsMinimized(prev => !prev);
  };
  // Simulate customer behavior with random insights (for demo purposes)
  useEffect(() => {
    if (!isEnabled) return;
    const suggestions = [{
      message: 'Try highlighting the exclusive experiences in this package',
      severity: 'info' as const,
      category: 'suggestion'
    }, {
      message: 'Customer may have budget concerns - emphasize value over price',
      severity: 'warning' as const,
      category: 'objection'
    }, {
      message: 'This customer shows analytical decision-making patterns - provide detailed comparisons',
      severity: 'info' as const,
      category: 'profile'
    }, {
      message: 'High interest in cultural activities - mention the traditional experiences',
      severity: 'success' as const,
      category: 'opportunity'
    }, {
      message: 'Customer is comparing multiple options - create urgency with limited availability',
      severity: 'warning' as const,
      category: 'strategy'
    }];
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
        addInsight({
          type: Math.random() > 0.5 ? 'suggestion' : 'behavior',
          message: randomSuggestion.message,
          severity: randomSuggestion.severity,
          category: randomSuggestion.category
        });
        // Randomly update readiness score
        const newScore = Math.min(100, customerProfile.readinessScore + Math.floor(Math.random() * 10) - 2);
        updateReadinessScore(Math.max(0, newScore));
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [isEnabled, customerProfile.readinessScore]);
  return <ClosingAssistantContext.Provider value={{
    isEnabled,
    isMinimized,
    activeTab,
    insights,
    customerProfile,
    recentActivity,
    closingReadiness,
    toggleEnabled,
    toggleMinimized,
    setActiveTab,
    dismissInsight,
    clearInsights,
    recordSectionView,
    recordInterestLevel,
    recordTimeSpent,
    updateReadinessScore,
    addRecentActivity
  }} data-id="element-4283">
      {children}
    </ClosingAssistantContext.Provider>;
};
export const useClosingAssistant = () => {
  const context = useContext(ClosingAssistantContext);
  if (context === undefined) {
    throw new Error('useClosingAssistant must be used within a ClosingAssistantProvider');
  }
  return context;
};